// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import CartPageTemplate, { CartPageTemplateStyles } from "@pace/App/Components/Cart/CartPageTemplate";
import XngZone from "@pace/App/Components/Common/XngZone";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { CHECKOUT_STEPS } from "@pace/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@pace/App/Widgets/CheckoutStepper/StepperContext";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

type Props = OwnProps & HasShellContext;

export interface CheckoutPageContainerStyles extends CartPageTemplateStyles {}

export const checkoutPageContainerStyles: CheckoutPageContainerStyles = {};

const styles = checkoutPageContainerStyles;

const CheckoutPageContainer: FC<Props> = ({ id, shellContext }) => {
    const { currentStep } = useContext(StepperContext);
    const showZone = (step: number) => shellContext.isEditing || currentStep === step;
    const { isLoading, isCheckoutShipping } = useAppSelector(state => {
        const {
            pages: {
                checkoutReviewAndSubmit: { cartId, isPlacingOrder, isPreloadingData: reviewIsPreloadingData },
                checkoutShipping: { isPreloadingData, isUpdatingCart, isUpdatingCustomer },
            },
            xCheckout: {
                shipping: { loading },
            },
        } = state as CustomAppState;
        const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
        return {
            isLoading:
                cartState.isLoading ||
                !cartState.value ||
                isPreloadingData ||
                isUpdatingCart ||
                isPlacingOrder ||
                reviewIsPreloadingData ||
                isUpdatingCustomer ||
                loading,
            isCheckoutShipping: getCurrentPage(state).type === "CheckoutShippingPage",
        };
    });

    return (
        <CartPageTemplate extendedStyles={styles} isCheckout={true}>
            <CartPageTemplate.MainSection>
                <LoadingOverlay
                    loading={isLoading}
                    css={css`
                        min-height: 300px;
                    `}
                >
                    <XngZone contentId={id} zoneName="Content0" hint="Zone Checkout Stepper" />
                    <Hidden hidden={!showZone(CHECKOUT_STEPS.SHIPPING) && !showZone(CHECKOUT_STEPS.PAYMENT)}>
                        <XngZone contentId={id} zoneName="Content1" hint="Zone For Shipping & Payment Step Content" />
                    </Hidden>
                    <Hidden hidden={!showZone(CHECKOUT_STEPS.REVIEW)}>
                        <XngZone contentId={id} zoneName="Content2" hint="Zone For Review Step Content" />
                    </Hidden>
                    {!isCheckoutShipping &&
                        createWidgetElement("CheckoutReviewAndSubmit/CheckoutReviewAndSubmitProductList", {
                            fields: {},
                        })}
                </LoadingOverlay>
            </CartPageTemplate.MainSection>
            <CartPageTemplate.CartTotalSection>
                <XngZone contentId={id} zoneName="Content3" hint="Zone For Checkout Total" />
                <Hidden hidden={!showZone(CHECKOUT_STEPS.REVIEW)}>
                    <XngZone contentId={id} zoneName="Content4" hint="Zone For Support" />
                </Hidden>
            </CartPageTemplate.CartTotalSection>
        </CartPageTemplate>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(CheckoutPageContainer),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Page Container",
        fieldDefinitions: [],
    },
};

export default widgetModule;
